import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import {
  Zap, ArrowRight, Star, ShieldCheck, CheckCircle2, XCircle, Copy, Download, Check,
  Hexagon, Orbit, Command, Triangle, Activity, Layers, Compass, Aperture, Quote,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const AGENCIES = [
  [Hexagon, "Northside Social"], [Orbit, "Atelier Media"], [Command, "Pulse & Co"], [Triangle, "Studio Gramme"],
  [Activity, "Orbital Creative"], [Layers, "Halftone Agency"], [Compass, "Meridian Digital"], [Aperture, "Form & Flux"],
];

const STATS = [
  ["15+ hrs", "back in your week", "No more Sunday-night caption marathons"],
  ["10×", "more brands per operator", "One person runs a full roster, calmly"],
  ["0", "auto-publish incidents", "Nothing ships without a human's hands"],
  ["3 layers", "before anything posts", "Brand rules → safeguards → your approval"],
];

const OLD_WAY = [
  "Sunday nights lost to caption spreadsheets",
  "Every draft sounds slightly off-brand — you rewrite it anyway",
  "Approvals buried in WhatsApp threads and email chains",
  "One banned word slips through. The client calls.",
  "A scheduler auto-posts to the wrong account at 2am",
];

const NEW_WAY = [
  "Brief once — drafts arrive already in your client's voice",
  "You edit the 10% that needs your taste, not the 90% that doesn't",
  "One inbox. Approve, request changes, reject. Full paper trail.",
  "Banned claims, PII and cross-client leaks caught before review",
  "You press publish. Natively. Every single time.",
];

const TESTIMONIALS = [
  {
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&q=60",
    name: "Marcus Reid", role: "Founder, Northside Social",
    quote: "I got my Sundays back. Six client brands, one operator, and the drafts actually sound like my clients — I stopped rewriting everything from scratch.",
    result: "6 brands · 1 operator",
  },
  {
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=160&q=60",
    name: "Priya Sharma", role: "Content Director, Atelier Media",
    quote: "The approval trail saved a client relationship. When they asked 'who signed this off?', I showed them the audit log. Meeting over in four minutes.",
    result: "100% audit coverage",
  },
  {
    img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&q=60",
    name: "Elena Kovacs", role: "Founder, building her own brand",
    quote: "I'm not an agency — Celebre treats my own profile like the client. My LinkedIn finally sounds like me on my best day, every day, and I still post everything myself.",
    result: "3 posts/wk, solo",
  },
];

const FAQS = [
  ["I'm not an agency — can I use it for my own profile?", "Absolutely. Set yourself up as your own client: your voice becomes the Brand Intelligence profile, the trend radar tracks your niche, and you get PR-level consistency on LinkedIn, X or Instagram — while remaining the only person who ever presses publish."],
  ["Will it ever post without my approval?", "Never. There is no publishing API connected — by design. Approved content lands in a Ready-to-Post queue on your phone; you copy the caption, download the sized media, and post natively yourself. You are always the last mile."],
  ["Will the drafts actually sound like my client?", "Each client gets a Brand Intelligence profile — voice, banned claims, legal rules, audience, keywords, competitors. Every draft is grounded in it. You'll edit for taste, not for damage control."],
  ["What stops something risky reaching a client?", "Three layers: the brand profile constrains the drafting, a safeguards engine scans for banned words, PII and cross-client leaks, and a human approves with a full audit trail. Plus a one-click kill switch."],
  ["How much does it cost me, really?", "You pay for what you use: a text draft ≈ 1 credit, an image ≈ 3, a video ≈ 15, an SEO article ≈ 10. Packs from $9. Every workspace starts with 25 free credits — no card required."],
  ["Can my clients approve content themselves?", "Yes — send them a secure portal link. They approve or request changes with comments, no account needed, and everything lands in the same audit trail."],
];

const Stars = () => (
  <span className="inline-flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-amber-400 text-amber-400" />)}</span>
);

const QueueMock = () => (
  <div className="w-[290px] bg-white rounded-2xl border border-zinc-200 shadow-[0_30px_70px_-20px_rgb(0,0,0,0.18)] p-5">
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-[0.18em]">Ready-to-Post · Aurora Coffee</p>
      <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Approved by you</span>
    </div>
    <p className="mt-3 text-[12px] leading-relaxed text-zinc-700">"New drop: Huila, Colombia — washed, floral, ridiculous in a V60. Small batch, Thursday."</p>
    <div className="mt-4 grid grid-cols-3 gap-1.5">
      <span className="flex items-center justify-center gap-1 bg-zinc-100 rounded-lg py-2 text-[10px] font-semibold text-zinc-700"><Copy size={11} /> Copy</span>
      <span className="flex items-center justify-center gap-1 bg-zinc-100 rounded-lg py-2 text-[10px] font-semibold text-zinc-700"><Download size={11} /> Media</span>
      <span className="flex items-center justify-center gap-1 bg-emerald-500 text-white rounded-lg py-2 text-[10px] font-bold"><Check size={11} /> Posted</span>
    </div>
    <p className="mt-3 text-[10px] text-zinc-400 flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-500" /> Safeguards passed · you press publish</p>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.removeEventListener("scroll", onScroll); };
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const CTA = ({ id, children, dark }) => (
    <button data-testid={id} onClick={() => navigate("/login")}
      className={`group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${dark ? "bg-white text-indigo-950 shadow-[0_16px_50px_rgb(0,0,0,0.3)]" : "bg-indigo-600 text-white shadow-[0_10px_30px_rgb(79,70,229,0.35)] hover:bg-indigo-500"}`}>
      {children} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 overflow-x-clip">
      {/* nav */}
      <header className={`fixed top-0 inset-x-0 z-50 backdrop-blur-2xl bg-white/70 border-b transition-shadow duration-300 ${scrolled ? "border-black/5 shadow-sm" : "border-transparent"}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center"><Zap size={15} className="text-white fill-white" /></span>
            <span className="font-display font-bold text-lg tracking-tight">Celebre</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-[13px] font-medium text-zinc-600">
            {[["Why Celebre", "why"], ["Stories", "stories"], ["Pricing", "pricing"], ["FAQ", "faq"]].map(([l, id]) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="hover:text-zinc-950 transition-colors">{l}</button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="landing-login-link" onClick={() => navigate("/login")} className="text-[13px] font-medium text-zinc-600 hover:text-zinc-950 transition-colors">Sign in</button>
            <button data-testid="landing-cta-header" onClick={() => navigate("/login")}
              className="px-4 py-2 rounded-full bg-indigo-600 text-white text-[13px] font-semibold shadow-[0_4px_14px_rgb(79,70,229,0.35)] hover:bg-indigo-500 hover:-translate-y-0.5 transition-[background-color,transform] duration-200">
              Get your week back
            </button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative pt-36 pb-14 md:pt-44 md:pb-20">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1000px 460px at 30% -10%, rgba(99,102,241,0.12), transparent 60%)" }} />
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <motion.p variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-zinc-200 text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-semibold shadow-sm">
              <ShieldCheck size={12} /> For agencies & personal brands
            </motion.p>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="mt-6 font-display font-extrabold text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
              You didn't build your name to spend it writing captions at midnight.
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 text-lg md:text-xl text-zinc-600 leading-relaxed max-w-xl">
              Whether you run ten client brands or one — your own — Celebre gives it a PR team's rigor: drafts in the right voice, safeguards, approvals and the paperwork handled. <span className="text-zinc-900 font-semibold">You always press publish.</span>
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap items-center gap-5">
              <CTA id="landing-cta-hero">Take back 15 hours a week</CTA>
              <button data-testid="landing-cta-demo" onClick={() => go("demo")} className="text-sm font-semibold text-zinc-600 hover:text-zinc-950 transition-colors">Poke around the live demo ↓</button>
            </motion.div>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-4 text-[12px] text-zinc-500">
              25 free credits · no card required · nothing auto-publishes, ever
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={5} className="mt-7 flex items-center gap-3 text-[13px] text-zinc-600" data-testid="hero-social-proof">
              <Stars /> <span className="font-semibold text-zinc-800">5.0</span>
              <span className="text-zinc-300">|</span> loved by 500+ agencies & founders
              <span className="text-zinc-300">|</span> zero platform-API risk
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative h-[360px] hidden md:block">
            <motion.div initial={{ opacity: 0, y: 40, rotate: 2 }} animate={{ opacity: 1, y: 0, rotate: 2 }} transition={{ duration: 0.7, delay: 0.35 }}
              className="absolute top-6 right-2"><QueueMock /></motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.8 }}
              className="absolute bottom-10 left-0 bg-white border border-zinc-200 rounded-2xl shadow-xl px-5 py-4">
              <p className="text-[11px] text-zinc-400 font-medium">Your Monday, 9:04am</p>
              <p className="text-[13px] font-semibold mt-1">12 drafts waiting for <span className="text-indigo-600">your</span> call.</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">Approve over coffee. Done by 9:30.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* stats trust bar */}
      <section className="border-y border-zinc-200 bg-white/60">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-zinc-200">
          {STATS.map(([big, label, sub], i) => (
            <motion.div key={label} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="px-8 py-9">
              <p className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-indigo-600">{big}</p>
              <p className="text-[13px] font-semibold text-zinc-900 mt-1">{label}</p>
              <p className="text-[12px] text-zinc-500 mt-1 leading-relaxed">{sub}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* marquee */}
      <section className="py-9 bg-white border-b border-zinc-100">
        <p className="text-center text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-semibold mb-6">Agencies with full rosters — and founders who are their own best client — run their week on Celebre</p>
        <Marquee gradient gradientColor="#ffffff" speed={34} autoFill>
          {AGENCIES.map(([Icon, name]) => (
            <span key={name} className="mx-10 inline-flex items-center gap-2.5 select-none">
              <span className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center"><Icon size={15} className="text-zinc-400" /></span>
              <span className="font-display font-bold text-sm tracking-wide text-zinc-400">{name}</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* who it's for */}
      <section className="max-w-6xl mx-auto px-6 pt-24 md:pt-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-indigo-600">Two ways people use it</p>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-tight">Client rosters. Or the brand that shares your name.</h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} data-testid="audience-agencies"
            className="bg-white border border-zinc-200 rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">For agencies</p>
            <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">Run the whole roster without dropping your standards.</h3>
            <p className="mt-3 text-[14px] text-zinc-600 leading-relaxed">Every client gets their own brain, their own calendar, their own approval trail — strictly walled off from each other. White-label reports and a client portal make you look bigger than you are.</p>
            <ul className="mt-5 space-y-2 text-[13px] text-zinc-600">
              {["Per-client Brand Intelligence with cross-client leak protection", "Approval inbox + shareable client portal", "Reports that carry your logo, not ours"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> {f}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} data-testid="audience-personal"
            className="bg-white border border-zinc-200 rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-indigo-600">For your own profile</p>
            <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">PR-level presence — without hiring a PR firm.</h3>
            <p className="mt-3 text-[14px] text-zinc-600 leading-relaxed">Founders, consultants, creators: set yourself up as your own best client. Your voice becomes the brand profile, the trend radar feeds your niche, and you show up consistently — even in your busiest weeks.</p>
            <ul className="mt-5 space-y-2 text-[13px] text-zinc-600">
              {["Your voice, learned once — from your own site if you like", "Trend radar + competitor watch for your niche", "A week of posts drafted in minutes, posted by you"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> {f}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* old vs new */}
      <section id="why" className="max-w-6xl mx-auto px-6 py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-indigo-600">Your week, before and after</p>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-tight">The work didn't change. Who carries it did.</h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 md:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400">Life before</p>
            <div className="mt-6 space-y-4">
              {OLD_WAY.map((t) => (
                <p key={t} className="flex gap-3 text-[14px] text-zinc-500 leading-relaxed"><XCircle size={17} className="text-zinc-300 shrink-0 mt-0.5" />{t}</p>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="relative bg-white border-2 border-indigo-500/60 rounded-3xl p-8 md:p-10 shadow-[0_24px_60px_-16px_rgb(79,70,229,0.25)]">
            <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-indigo-500/20 blur-3xl" />
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-indigo-600">Your week with Celebre</p>
            <div className="mt-6 space-y-4">
              {NEW_WAY.map((t) => (
                <p key={t} className="flex gap-3 text-[14px] text-zinc-800 font-medium leading-relaxed"><CheckCircle2 size={17} className="text-emerald-500 shrink-0 mt-0.5" />{t}</p>
              ))}
            </div>
          </motion.div>
        </div>
        <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-8 text-[13px] text-zinc-500">
          The co-pilot carries the 70% that's repetitive. You keep the 30% that clients actually pay you for.
        </motion.p>
      </section>

      {/* benefit bento */}
      <section className="bg-white border-y border-zinc-100 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-indigo-600">Built around your judgment</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-tight">Every screen exists to protect your name on the work.</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="md:col-span-8 bg-[#FAFAFA] border border-zinc-200 rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Your client's voice, kept safe</p>
              <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">Teach it once. Never explain the brand again.</h3>
              <p className="mt-3 text-[14px] text-zinc-600 leading-relaxed max-w-lg">Voice, banned claims, legal rules, audience — captured in a living profile per client. It can even learn from their website. Every draft you review already respects the rules you'd enforce anyway.</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["warm, craft-obsessed", "no health claims", "never discount-led", "audience: urban 25-40", "competitors blocked", "es + en"].map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-[11px] font-medium text-zinc-600">{t}</span>
                ))}
              </div>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="md:col-span-4 bg-zinc-950 text-white rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow] duration-300">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">You stay the last mile</p>
              <h3 className="mt-3 font-display font-bold text-2xl tracking-tight leading-snug">Approve on the couch. Post from your phone.</h3>
              <p className="mt-3 text-[14px] text-zinc-400 leading-relaxed">The mobile queue hands you the caption and sized media. You post natively — the account keys never leave your pocket.</p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="md:col-span-4 bg-[#FAFAFA] border border-zinc-200 rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Sleep insurance</p>
              <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">Nothing risky reaches a client.</h3>
              <p className="mt-3 text-[14px] text-zinc-600 leading-relaxed">Banned words, PII and cross-client leaks get caught before review — and there's a kill switch if you ever want everything to stop.</p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="md:col-span-8 bg-[#FAFAFA] border border-zinc-200 rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Walk into every client call armed</p>
              <h3 className="mt-3 font-display font-bold text-2xl tracking-tight">Reports with your logo. Strategy you didn't stay up writing.</h3>
              <p className="mt-3 text-[14px] text-zinc-600 leading-relaxed max-w-lg">Import the numbers, get findings and next moves written for you, and hand over a white-label report that looks like a strategist's weekend — competitor gaps and trend calls included.</p>
              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                {[["Impressions", "159k"], ["Eng. rate", "4.6%"], ["Next move", "Feed X more"]].map(([l, v]) => (
                  <div key={l} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{l}</p>
                    <p className="font-display font-bold text-lg mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* testimonials */}
      <section id="stories" className="max-w-6xl mx-auto px-6 py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-indigo-600">From people like you</p>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-tight">The hours came back. The standards didn't drop.</h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={`testimonial-${i}`}
              className="bg-white border border-zinc-200 rounded-3xl p-8 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              <Quote size={22} className="text-indigo-200" />
              <p className="mt-4 text-[14px] text-zinc-700 leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-zinc-200" />
                <div className="flex-1">
                  <p className="text-[13px] font-bold">{t.name}</p>
                  <p className="text-[11px] text-zinc-500">{t.role}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
                <Stars />
                <span className="text-[11px] font-bold text-indigo-600">{t.result}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* pricing */}
      <section id="pricing" className="bg-white border-y border-zinc-100 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-indigo-600">Pricing</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-tight">Cheaper than one hour of your time.</h2>
            <p className="mt-4 text-[14px] text-zinc-600">You only pay for what the co-pilot produces: draft ≈ 1 credit · image ≈ 3 · article ≈ 10 · video ≈ 15. Start with 25 free.</p>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
            {[
              ["Starter", 9, 50, "Your own profile, or a first client brand", false],
              ["Growth", 29, 200, "The sweet spot: 3–10 brands, clients' or yours", true],
              ["Scale", 59, 500, "Full teams shipping daily for a roster", false],
            ].map(([name, price, cr, desc, hot], i) => (
              <motion.div key={name} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className={`relative bg-white rounded-3xl p-8 md:p-10 flex flex-col hover:-translate-y-1 transition-[transform,box-shadow] duration-300 ${hot ? "border-2 border-indigo-500 shadow-[0_24px_60px_-16px_rgb(79,70,229,0.3)] md:scale-[1.04]" : "border border-zinc-200 hover:shadow-xl"}`}>
                {hot && <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-indigo-600 text-white text-[10px] uppercase tracking-[0.15em] font-bold">Most agencies pick this</span>}
                <h3 className="font-display font-bold text-lg">{name}</h3>
                <p className="mt-4"><span className="font-display font-extrabold text-5xl tracking-tight">${price}</span><span className="text-zinc-400 text-sm"> / pack</span></p>
                <p className="mt-1 text-sm font-semibold text-indigo-600">{cr} credits ≈ {cr} drafts</p>
                <p className="mt-3 text-[13px] text-zinc-500 leading-relaxed">{desc}</p>
                <ul className="mt-5 space-y-2.5 text-[13px] text-zinc-600 flex-1">
                  {["Unlimited clients & teammates", "All 12 modules, no gates", "White-label reports with your logo", "Client approval portal"].map((f) => (
                    <li key={f} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <button data-testid={`pricing-cta-${String(name).toLowerCase()}`} onClick={() => navigate("/login")}
                  className={`mt-8 w-full py-3 rounded-full text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${hot ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}>
                  Start with {name}
                </button>
                <p className="mt-3 text-center text-[11px] text-zinc-400">25 free credits first · no card required</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-bold text-3xl md:text-4xl tracking-tighter">The questions you're already asking.</motion.h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-[15px] font-semibold hover:no-underline hover:text-indigo-600 transition-colors">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* demo callout */}
      <section id="demo" className="max-w-6xl mx-auto px-6 pb-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="bg-zinc-950 text-white rounded-3xl px-8 md:px-14 py-12 md:py-14 flex flex-col md:flex-row items-start md:items-center gap-8">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-bold">See it with real client data</p>
            <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl tracking-tight">Two demo brands are already loaded. Go judge it yourself.</h3>
            <p className="mt-2 text-[14px] text-zinc-400">Drafts, approvals, insights, competitor research — all live. Break nothing, learn everything.</p>
          </div>
          <div className="shrink-0 space-y-3">
            <div data-testid="demo-credentials" className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 font-mono text-[13px]">
              <p className="text-zinc-400">login: <span className="text-white">demo@celebre.app</span></p>
              <p className="text-zinc-400">pass: <span className="text-white">Demo123!</span></p>
            </div>
            <CTA id="landing-cta-demo-block" dark>Open the demo studio</CTA>
          </div>
        </motion.div>
      </section>

      {/* final CTA */}
      <section className="bg-indigo-950 py-28 md:py-36 relative overflow-hidden">
        <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(700px 300px at 80% 120%, rgba(129,140,248,0.25), transparent 60%)" }} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-white">
            Be the strategist your brand deserves.<br /><span className="text-indigo-300">Whether the brand is a client's — or yours.</span>
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="mt-6 text-indigo-200/80 text-base max-w-lg mx-auto">
            Your first week of client content, done before lunch — and every word of it still yours to approve.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className="mt-10">
            <CTA id="landing-cta-bottom" dark>Start free — 25 credits</CTA>
            <p className="mt-4 text-[12px] text-indigo-300/60">No card. No auto-publishing. No excuses for Sunday captions.</p>
          </motion.div>
        </div>
      </section>

      <footer className="bg-indigo-950 border-t border-white/10 py-8 text-center text-[12px] text-indigo-300/60">
        Celebre — the co-pilot for agencies and personal brands · Content is AI-assisted and human-approved. You always press publish.
      </footer>
    </div>
  );
}
